/**
 * Google Maps loader. The key and map id come from the API (`uiConfig`), the
 * libraries load once and are shared by the area dialog and the map view.
 */
import { importLibrary, setOptions } from '@googlemaps/js-api-loader'
import { api, data } from '../api/http'
import type { UiConfig } from '../api/types'

export type GoogleMapsLibraries = { core: google.maps.CoreLibrary; maps: google.maps.MapsLibrary; marker: google.maps.MarkerLibrary; mapId: string }

let libraryPromise: Promise<GoogleMapsLibraries> | null = null

export function loadGoogleMaps(): Promise<GoogleMapsLibraries> {
  if (!libraryPromise) {
    libraryPromise = api('uiConfig').then(async envelope => {
      const config = data<UiConfig>(envelope)
      const key = config.googleMapsJavaScriptApiKey
      const mapId = config.googleMapsMapId
      if (typeof key !== 'string' || key.trim() === '') throw new Error('Google Maps API key is not configured for this host')
      if (typeof mapId !== 'string' || mapId.trim() === '') throw new Error('Google Maps map ID is not configured')
      setOptions({ key: key.trim(), v: 'weekly' })
      const [core, maps, marker] = await Promise.all([
        importLibrary('core') as Promise<google.maps.CoreLibrary>,
        importLibrary('maps') as Promise<google.maps.MapsLibrary>,
        importLibrary('marker') as Promise<google.maps.MarkerLibrary>,
      ])
      return { core, maps, marker, mapId: mapId.trim() }
    }).catch((error: unknown) => { libraryPromise = null; throw error })
  }
  return libraryPromise
}

/** Read a token for map chrome; the map is the one place that needs a literal colour string. */
export function themeToken(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

export function isDarkTheme(): boolean {
  return document.documentElement.getAttribute('data-theme') !== 'light'
}
